import { useCallback, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { Spin, Alert } from "antd"
import { TempLayout } from "@/layouts"
import { usaSapAssetStore } from "@/stores/sap-asset/sapAssetStore"
import SapAssetDetailView from "./components/SapAssetDetailView"
import ApprovalActions from "@/atoms/approval-actions"
import { useNavigate } from "react-router"
import { ISapAssetDetail } from "@/types/sapAsset.type"
import { IApprovalProccessId } from "@/types"
import { getAssetEditAccess } from "./utils/assetEditAccess"

interface ISapSanitizedAsset {
  approval_process_id?: {
    status: number
    data: IApprovalProccessId
  }
  assets?: ISapAssetDetail[]
  [key: string]: unknown
}

const SapAssetDetail = () => {
  const { recordId } = useParams()
  const navigate = useNavigate()
  const { fetchSapAssetById, currentAsset, isLoading, error } = usaSapAssetStore()
  const currentEmployeeId = useMemo(() => {
    try {
      const currentSession = JSON.parse(window.localStorage.getItem("session_farms") || "null") as { employee_id?: string } | null
      return currentSession?.employee_id || null
    } catch (_error) {
      return null
    }
  }, [])

  const sanitizedApprovalData = currentAsset
    ? {
        status: 200,
        data: currentAsset.approval_process_id,
      }
    : undefined

  const sanitizedAssetDetail: ISapSanitizedAsset | null = currentAsset
    ? {
        ...currentAsset,
        approval_process_id: sanitizedApprovalData,
      }
    : null
    
  const editAccess = useMemo(
    () => getAssetEditAccess(currentAsset?.approval_process_id, currentEmployeeId),
    [currentAsset?.approval_process_id, currentEmployeeId],
  )
  const isApproved = (currentAsset?.approval_status || "").toUpperCase() === "APPROVED"

  const handleApproveClick = () => {
    navigate("/sapasst/history")
  }

  const fetchAssetById = useCallback(async () => {
    if (recordId) {
      await fetchSapAssetById(recordId)
    }
  }, [recordId, fetchSapAssetById])

  useEffect(() => {
    void fetchAssetById()
  }, [fetchAssetById])

  useEffect(() => {
    const requesterName = currentAsset?.fullname?.trim()
    const currentRecordId = currentAsset?.record_id || recordId || ""
    document.title = requesterName ? `${requesterName} - SAP Asset${currentRecordId ? ` (${currentRecordId})` : ""}` : "SAP - Asset Detail"

    return () => {
      document.title = "FARMS Application"
    }
  }, [currentAsset?.fullname, currentAsset?.record_id, recordId])

  return (
    <TempLayout title="SAP - Asset Detail" navNavigateUrl="/sapasst/history">
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : sanitizedAssetDetail ? (
        <SapAssetDetailView data={sanitizedAssetDetail} />
      ) : (
        <Alert message="Not Found" description="Asset not found or ID missing" type="warning" showIcon />
      )}

      {currentAsset && (
        <ApprovalActions
          canEditData={!isApproved && editAccess.canEdit}
          editNavigateUrl={`/sapasst/edit/${recordId}`}
          mainData={sanitizedAssetDetail}
          onApproveClick={handleApproveClick}
        />
      )}
    </TempLayout>
  )
}

export default SapAssetDetail
